import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/App.css';
import { initGA, sendPageView } from './utils/analytics';

import Landing from './components/Landing';
import Dashboard from './components/Dashboard';
import GroqTutorial from './components/GroqTutorial';
import BlogPost from './components/BlogPost';
import BlogAdmin from './components/BlogAdmin';
import AllNews from './components/AllNews';
import { authUrl } from './utils/apiUrl';


function App() {
  const [user, setUser] = useState(null);
  const [authCheckDone, setAuthCheckDone] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname !== '/app') {
      setAuthCheckDone(true);
      return;
    }
    let cancelled = false;
    let retryCount = 0;
    const MAX_RETRIES = 2; // Máximo 2 reintentos para evitar bucles infinitos
    
    // Pequeño delay para asegurar que la cookie de sesión esté disponible después del redirect de Google
    const checkAuth = () => {
      fetch(authUrl('/auth/me'), { credentials: 'include', cache: 'no-store' })
        .then((r) => {
          if (cancelled) return;
          if (r.status === 401) {
            // Si es 401, redirigir inmediatamente a login (no hacer retry)
            setUser(null);
            setAuthCheckDone(true);
            navigate('/', { replace: true });
            return;
          }
          if (!r.ok) {
            setAuthCheckDone(true);
            return;
          }
          return r.json();
        })
        .then((data) => {
          if (cancelled) return;
          if (data) {
            setUser(data);
            setAuthCheckDone(true);
          } else {
            setAuthCheckDone(true);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          
          // Si es un error de autenticación (401), redirigir inmediatamente
          if (err?.message === 'unauth' || err?.message?.includes('401')) {
            setUser(null);
            setAuthCheckDone(true);
            navigate('/', { replace: true });
            return;
          }
          
          // Para otros errores, solo hacer retry si es redirect de Google y no excedemos el límite
          const isGoogleRedirect = document.referrer.includes('accounts.google.com');
          if (isGoogleRedirect && retryCount < MAX_RETRIES) {
            retryCount++;
            setTimeout(() => {
              if (!cancelled) checkAuth();
            }, 500);
          } else {
            setUser(null);
            setAuthCheckDone(true);
            if (!isGoogleRedirect) {
              navigate('/', { replace: true });
            }
          }
        });
    };
    
    // Si viene del redirect de Google, dar un pequeño delay para que la cookie esté lista
    const isGoogleRedirect = document.referrer.includes('accounts.google.com');
    if (isGoogleRedirect) {
      setTimeout(checkAuth, 200);
    } else {
      checkAuth();
    }
    
    return () => { cancelled = true; };
  }, [location.pathname, navigate]);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/all-news" element={<AllNews />} />
      <Route path="/groq-tutorial" element={<GroqTutorial />} />
      <Route path="/news/:categoryPath/:slug" element={<BlogPost />} />
      <Route path="/noticias/:categoryPath/:slug" element={<BlogPost />} />
      <Route
        path="/app"
        element={
          location.pathname === '/app' ? (
            user ? (
              <Dashboard user={user} onLogout={() => setUser(null)} />
            ) : (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Verificando sesión...</span>
                </div>
                <p className="mt-3">Verificando sesión...</p>
              </div>
            )
          ) : null
        }
      />
      <Route
        path="/app/blog-admin"
        element={<BlogAdmin />}
      />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

export default App;
